import { FC } from "react";

import { getTranslationLabelLanguage } from "../../helpers/loadTranslationLabels";
import Stack from "@mui/material/Stack";
import { Column, Locales } from "@simplyk/common";
import { Control, Path, UseFormSetValue, useFormContext } from "react-hook-form";

import { TranslatedTextField } from "../../sharedComponents/TranslatedTextField";
import { TranslationCard } from "../../sharedComponents/TranslationCard";
import { useTranslationLocales } from "../../Context/TranslationLocaleContext";
import { ShopVariantTranslationFieldData, TicketingTranslationsData } from "../../translation.types";

import { ShopVariantValueTranslationRow } from "./ShopVariantValueTranslationRow";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";

interface ShopVariantTranslationCardProps {
  control: Control<TicketingTranslationsData>;
  setValue: UseFormSetValue<TicketingTranslationsData>;
  variantIndex: number;
  fieldData: ShopVariantTranslationFieldData;
  translationData: ShopVariantTranslationFieldData | undefined;
  translationLocale: Locales;
  isTranslationColumn: boolean;
}

export const ShopVariantTranslationCard: FC<ShopVariantTranslationCardProps> = ({
  control,
  setValue,
  variantIndex,
  fieldData,
  translationData,
  translationLocale,
  isTranslationColumn,
}) => {
  const { t } = useTranslate();
  const { register } = useFormContext<TicketingTranslationsData>();
  const locales = useTranslationLocales();
  const fieldLocale = getTranslationLabelLanguage(isTranslationColumn ? translationLocale : locales?.formLocale);

  const column = isTranslationColumn ? Column.Translations : Column.Fields;
  const variantIdPath = `${column}.shopVariantsFields.${variantIndex}.variantId` as Path<TicketingTranslationsData>;
  const variantNamePath = `${column}.shopVariantsFields.${variantIndex}.name` as Path<TicketingTranslationsData>;

  return (
    <TranslationCard
      title={(isTranslationColumn ? translationData?.name : fieldData.name) || fieldData.name || `#${variantIndex + 1}`}
    >
      <input type="hidden" {...register(variantIdPath)} value={fieldData.variantId} />

      {isTranslationColumn ? (
        <TranslatedTextField
          fieldName={variantNamePath}
          defaultFieldValue={fieldData.name}
          defaultTranslationFieldValue={translationData?.name || undefined}
          setValue={setValue}
          translationLocale={translationLocale}
          label={t("dashboard", "translation.shopVariantName", { lng: fieldLocale })}
          control={control}
          errorMessage={undefined}
          dataTest={`shop-variant-${variantIndex}-name-translation`}
        />
      ) : (
        <DashboardTextField
          label={t("dashboard", "translation.shopVariantName", { lng: fieldLocale })}
          control={control}
          name={variantNamePath}
          defaultValue={fieldData.name}
          data-test={`shop-variant-${variantIndex}-name-field`}
        />
      )}

      <Stack gap={2}>
        {fieldData.values.map((value, valueIndex) => {
          const translationValue = translationData?.values.find(
            (translatedValue) => translatedValue.valueId === value.valueId
          );

          return (
            <ShopVariantValueTranslationRow
              key={value.valueId}
              control={control}
              setValue={setValue}
              column={column}
              variantIndex={variantIndex}
              valueIndex={valueIndex}
              fieldValue={value}
              translationValue={translationValue}
              translationLocale={translationLocale}
              isTranslationColumn={isTranslationColumn}
              label={t("dashboard", "translation.optionNumber", { lng: fieldLocale, index: valueIndex + 1 })}
            />
          );
        })}
      </Stack>
    </TranslationCard>
  );
};
